// The environment figures of section 8 (spec/api-conventions.md section 20.3): segment sizes, A/B results, revenue by
// day, the refresh job and the A/B apply command — read through the operations registry like everything else. The A/B
// planner and the primary metric (spec/dashboard.md 8.4; spec/liveops-engine.md 11.2): the required sample per variant
// (two proportions, or a mean with a standard deviation), the headline of a comparison and a test's progress.
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import { ops, type ResponseOf } from '../../api/operations';
import type { Environment, Job } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';

export type SegmentSizes = ResponseOf<typeof ops.getSegmentSizes>;
export type AbTestResults = ResponseOf<typeof ops.getAbTestResults>;
export type RevenueByDay = ResponseOf<typeof ops.getRevenue>;
export type AbTestApplyResult = ResponseOf<typeof ops.applyAbTest>;
export type AnalyticsDaily = ResponseOf<typeof ops.getAnalyticsDaily>;

interface Remote<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  reload: () => void;
}

function useRemote<T>(key: string | null, load: () => Promise<T>): Remote<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (key === null) return;
    // no identity-based dedupe here: StrictMode's mount → unmount → mount would otherwise discard the only load
    let alive = true;
    setLoading(true);
    Promise.resolve()
      .then(load)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setError(null);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setError(e instanceof ApiError ? e : new ApiError(0, 'unavailable', String(e), ''));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [key, tick, load]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, loading, error, reload };
}

/** The segment sizes of an environment (8.1), and the refresh job. */
export function useSegmentSizes(env: Environment | null): Remote<SegmentSizes> & { refresh: () => Promise<Job> } {
  const { client } = useAuth();
  const { org, game } = useGame();
  const slug = env?.slug ?? null;
  const load = useCallback(() => client.data(ops.getSegmentSizes, { path: { org, game: game.slug, env: slug ?? '' } }), [client, org, game.slug, slug]);
  const remote = useRemote(slug ? `segments|${org}|${game.slug}|${slug}` : null, load);
  const refresh = useCallback(() => client.data(ops.refreshSegmentSizes, { path: { org, game: game.slug, env: slug ?? '' } }) as Promise<Job>, [client, org, game.slug, slug]);
  return { ...remote, refresh };
}

/**
 * The users, activity, money and statistics per variant of a test in an environment (8.4; spec/api-conventions.md 21.7):
 * `branch` names the branch whose copy of the test gives the variants' order and weights (the control first, the SRM check).
 */
export function useAbTestResults(env: Environment | null, testId: string | null, opts: { branch?: string | null; includeTesters?: boolean } = {}): Remote<AbTestResults> {
  const { client } = useAuth();
  const { org, game } = useGame();
  const slug = env?.slug ?? null;
  const branch = opts.branch || null;
  const testers = !!opts.includeTesters;
  const load = useCallback(
    () => client.data(ops.getAbTestResults, { path: { org, game: game.slug, env: slug ?? '', test: testId ?? '' }, query: { branch, includeTesters: testers ? 'true' : null } }),
    [client, org, game.slug, slug, testId, branch, testers],
  );
  return useRemote(slug && testId && !testId.startsWith('$') ? `results|${org}|${game.slug}|${slug}|${testId}|${branch ?? ''}|${testers}` : null, load);
}

/** The daily figures of an environment over game-time days (8.5; spec/api-conventions.md 21.7). */
export function useAnalyticsDaily(env: Environment | null, from: string, to: string, includeTesters: boolean): Remote<AnalyticsDaily> {
  const { client } = useAuth();
  const { org, game } = useGame();
  const slug = env?.slug ?? null;
  const load = useCallback(
    () => client.data(ops.getAnalyticsDaily, { path: { org, game: game.slug, env: slug ?? '' }, query: { from, to, includeTesters: includeTesters ? 'true' : null } }),
    [client, org, game.slug, slug, from, to, includeTesters],
  );
  return useRemote(slug ? `daily|${org}|${game.slug}|${slug}|${from}|${to}|${includeTesters}` : null, load);
}

/** The claimed, non-test orders per game-time day and currency of an environment (8.5; the claim's day since M7 slice 7). */
export function useRevenue(env: Environment | null, from: string, to: string): Remote<RevenueByDay> {
  const { client } = useAuth();
  const { org, game } = useGame();
  const slug = env?.slug ?? null;
  const load = useCallback(() => client.data(ops.getRevenue, { path: { org, game: game.slug, env: slug ?? '' }, query: { from, to } }), [client, org, game.slug, slug, from, to]);
  return useRemote(slug ? `revenue|${org}|${game.slug}|${slug}|${from}|${to}` : null, load);
}

/** The A/B apply command (8.4): the winner as one commit. */
export async function applyAbTest(client: ReturnType<typeof useAuth>['client'], org: string, game: string, branch: string, testId: string, variantId: string, message?: string): Promise<AbTestApplyResult> {
  return client.data(ops.applyAbTest, { path: { org, game, branch, test: testId }, body: { variant: variantId, message: message ?? null } });
}

/** `YYYY-MM-DD` of a unix instant in UTC. */
export function utcDate(seconds: number): string {
  return new Date(seconds * 1000).toISOString().slice(0, 10);
}

/** The minor units per major unit of a currency (1 for the zero-decimal ones). */
export function minorOf(currency: string): number {
  return /^(JPY|KRW|HUF|CLP|ISK|VND)$/i.test(currency) ? 1 : 100;
}

/** `12.99 USD` from minor units. */
export function moneyText(amount: number, currency: string): string {
  const minor = minorOf(currency);
  const n = amount / minor;
  return `${minor === 1 ? String(n) : n.toFixed(2)} ${currency}`;
}

// ---- the primary metric and the required sample (spec/dashboard.md 8.4; spec/liveops-engine.md 11.2) ---------------------

/**
 * The primary metrics of a test. The ABTest template's `metric` is a free string (Balancy leaves it empty); the dashboard
 * writes one of these keys, and an empty or unknown value reads as conversion. `proportion` metrics use the two-proportion
 * sample size, `mean` metrics the normal approximation with a standard deviation.
 */
export const AB_METRICS = [
  { key: 'conversion', label: 'Conversion', kind: 'proportion', help: 'Assigned players with a claimed, non-test order, over the assigned players' },
  { key: 'arpu', label: 'ARPU', kind: 'mean', help: 'Revenue per assigned player' },
  { key: 'revenue', label: 'Revenue per exposed user', kind: 'mean', help: 'Revenue over the players who saw the variant (exposed)' },
] as const;

export type AbMetricKey = (typeof AB_METRICS)[number]['key'];
export type AbMetric = (typeof AB_METRICS)[number];

/** The metric of a test's `metric` string (conversion when empty or unknown). */
export function metricOf(value: unknown): AbMetric {
  const key = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return AB_METRICS.find((m) => m.key === key) ?? AB_METRICS[0];
}

/** The planner defaults when the test leaves them 0 (the imported tests do): uplift 10 %, α 0.05, β 0.2 (power 0.8). */
export const AB_DEFAULTS = { uplift: 0.1, alpha: 0.05, betta: 0.2 } as const;

/** The test's planner figures, its zeros read as the defaults. */
export function plannerOf(data: Record<string, unknown>): { uplift: number; alpha: number; betta: number } {
  const pos = (v: unknown, fallback: number, max = 1) => {
    const x = Number(v);
    return Number.isFinite(x) && x > 0 && x < max ? x : fallback;
  };
  return { uplift: pos(data.uplift, AB_DEFAULTS.uplift, Infinity), alpha: pos(data.alphaGlobal ?? data.alpha, AB_DEFAULTS.alpha), betta: pos(data.betta, AB_DEFAULTS.betta) };
}

/** The standard normal quantile (Acklam's rational approximation, relative error below 1.2e-9). */
export function normalQuantile(p: number): number {
  if (!(p > 0 && p < 1)) return p === 0 ? -Infinity : p === 1 ? Infinity : NaN;
  const a = [-39.69683028665376, 220.9460984245205, -275.9285104469687, 138.357751867269, -30.66479806614716, 2.506628277459239];
  const b = [-54.47609879822406, 161.5858368580409, -155.6989798598866, 66.80131188771972, -13.28068155288572];
  const c = [-0.007784894002430293, -0.3223964580411365, -2.400758277161838, -2.549732539343734, 4.374664141464968, 2.938163982698783];
  const d = [0.007784695709041462, 0.3224671290700398, 2.445134137142996, 3.754408661907416];
  const lo = 0.02425;
  if (p < lo) {
    const q = Math.sqrt(-2 * Math.log(p));
    return (((((c[0]! * q + c[1]!) * q + c[2]!) * q + c[3]!) * q + c[4]!) * q + c[5]!) / ((((d[0]! * q + d[1]!) * q + d[2]!) * q + d[3]!) * q + 1);
  }
  if (p > 1 - lo) return -normalQuantile(1 - p);
  const q = p - 0.5;
  const r = q * q;
  return ((((((a[0]! * r + a[1]!) * r + a[2]!) * r + a[3]!) * r + a[4]!) * r + a[5]!) * q) / (((((b[0]! * r + b[1]!) * r + b[2]!) * r + b[3]!) * r + b[4]!) * r + 1);
}

/** (z_{1−α/2} + z_{1−β})²: the two-sided test at α with power 1 − β. */
function zSquared(alpha: number, betta: number): number {
  const z = normalQuantile(1 - alpha / 2) + normalQuantile(1 - betta);
  return z * z;
}

/**
 * The players per variant to detect a relative `uplift` of a conversion `p1` (two-proportion z-test, two-sided):
 * n = (z_{1−α/2} + z_{1−β})² · (p₁(1−p₁) + p₂(1−p₂)) / (p₂ − p₁)², p₂ = p₁ · (1 + uplift). Null when not estimable.
 */
export function sampleSizeProportion(p1: number, uplift: number, alpha: number, betta: number): number | null {
  const p2 = p1 * (1 + uplift);
  if (!(p1 > 0 && p1 < 1) || !(p2 > 0 && p2 < 1) || uplift === 0 || !(alpha > 0 && alpha < 1) || !(betta > 0 && betta < 1)) return null;
  return Math.ceil((zSquared(alpha, betta) * (p1 * (1 - p1) + p2 * (1 - p2))) / ((p2 - p1) * (p2 - p1)));
}

/**
 * The players per variant to detect a relative `uplift` of a mean (normal approximation, equal variances):
 * n = 2 · (z_{1−α/2} + z_{1−β})² · σ² / (μ · uplift)². Null when not estimable (no baseline or no σ).
 */
export function sampleSizeMean(mean: number, sd: number, uplift: number, alpha: number, betta: number): number | null {
  const delta = mean * uplift;
  if (!(mean > 0) || !(sd > 0) || delta === 0 || !(alpha > 0 && alpha < 1) || !(betta > 0 && betta < 1)) return null;
  return Math.ceil((2 * zSquared(alpha, betta) * sd * sd) / (delta * delta));
}

/**
 * The per-player standard deviation of revenue from what the results carry (conversion c, ARPPU m): assuming a payer's
 * spend has a standard deviation equal to its mean, E[X²] = c · 2m², so σ² = c · m² · (2 − c).
 */
export function revenueSdOf(conversion: number, arppu: number): number | null {
  if (!(conversion > 0 && conversion <= 1) || !(arppu > 0)) return null;
  return Math.sqrt(conversion * arppu * arppu * (2 - conversion));
}

/** The days until every variant has `required` players at `perDay` new players per variant a day (null without a pace). */
export function daysFor(required: number, have: number, perDay: number): number | null {
  if (!(perDay > 0)) return null;
  return Math.max(0, Math.ceil((required - have) / perDay));
}

/** `14 750` (thin-space thousands, the house style of large counts in prose). */
export function countText(n: number): string {
  return Math.round(n).toLocaleString('en-US').replace(/,/g, ' ');
}

type VariantRow = AbTestResults['variants'][number];
type StatRow = AbTestResults['stats'][number];

export interface Headline {
  metric: AbMetric;
  /** the control's and the variant's figure, as text */
  control: string;
  variant: string;
  /** the difference as text (pp for conversion, money otherwise), null without data */
  lift: string | null;
  /** the 95 % interval as text, when the server gives one */
  interval: string | null;
  p: number | null;
}

const signedText = (x: number, digits = 1) => {
  const text = Math.abs(x).toFixed(digits);
  if (Number(text) === 0) return (0).toFixed(digits);
  return `${x > 0 ? '+' : '−'}${text}`;
};

/** Per currency: amount (minor units) / players, as `{ currency, amount }`. */
function perUser(rows: { currency: string; amount: number | string }[], players: number): { currency: string; amount: number }[] {
  return players > 0 ? rows.map((r) => ({ currency: r.currency, amount: Number(r.amount) / players })) : [];
}

const moneyList = (rows: { currency: string; amount: number }[]) => (rows.length ? rows.map((r) => moneyText(r.amount, r.currency)).join(', ') : '—');

/** The headline figure of a comparison for the test's primary metric (8.4): the two values, the lift, the interval and p. */
export function headlineOf(metric: AbMetric, control: VariantRow | undefined, variant: VariantRow | undefined, stat: StatRow): Headline {
  if (metric.key === 'conversion') {
    const pct = (r: VariantRow | undefined) => (r && r.conversion !== null && r.conversion !== undefined ? `${(Number(r.conversion) * 100).toFixed(1)} %` : '—');
    const delta = stat.conversionDelta === null || stat.conversionDelta === undefined ? null : Number(stat.conversionDelta);
    const ci = stat.conversionInterval;
    return {
      metric,
      control: pct(control),
      variant: pct(variant),
      lift: delta === null ? null : `${signedText(delta)} pp`,
      interval: ci ? `${signedText(Number(ci.low))} to ${signedText(Number(ci.high))} pp` : null,
      p: stat.conversionP === null || stat.conversionP === undefined ? null : Number(stat.conversionP),
    };
  }
  if (metric.key === 'arpu') {
    const arpu = (r: VariantRow | undefined) => moneyList((r?.arpu ?? []).map((a) => ({ currency: a.currency, amount: Number(a.amount) })));
    const deltas = stat.arpuDelta ?? [];
    const first = deltas.find((a) => a.p !== null && a.p !== undefined) ?? deltas[0];
    return {
      metric,
      control: arpu(control),
      variant: arpu(variant),
      lift: deltas.length ? deltas.map((a) => `${Number(a.amount) < 0 ? '−' : '+'}${moneyText(Math.abs(Number(a.amount)), a.currency)}`).join(' · ') : null,
      interval: null,
      p: first && first.p !== null && first.p !== undefined ? Number(first.p) : null,
    };
  }
  // revenue per exposed user: computed here from the revenue and the exposure (the server has no test for it)
  const c = perUser(control?.revenue ?? [], Number(control?.exposed ?? 0));
  const v = perUser(variant?.revenue ?? [], Number(variant?.exposed ?? 0));
  const lifts = v.map((x) => ({ currency: x.currency, amount: x.amount - (c.find((y) => y.currency === x.currency)?.amount ?? 0) }));
  return {
    metric,
    control: moneyList(c),
    variant: moneyList(v),
    lift: lifts.length ? lifts.map((a) => `${a.amount < 0 ? '−' : '+'}${moneyText(Math.abs(a.amount), a.currency)}`).join(' · ') : null,
    interval: null,
    p: null,
  };
}

export interface Progress {
  /** the players of the slowest variant */
  have: number;
  /** the required players per variant, null when not estimable */
  required: number | null;
  /** new players per variant a day at the current pace, null before two days of assignments */
  perDay: number | null;
  daysLeft: number | null;
  /** why the requirement is not estimable */
  reason: string | null;
}

/**
 * The progress of a test against its required sample (8.4): the Control's observed baseline of the primary metric, the
 * test's own uplift / α / β (the defaults for zeros), the slowest variant's players and the pace of the assignments.
 */
export function progressOf(metric: AbMetric, results: AbTestResults, planner: { uplift: number; alpha: number; betta: number }): Progress {
  const variants = results.variants ?? [];
  const control = variants[0];
  const have = variants.length ? Math.min(...variants.map((v) => Number(v.users ?? 0))) : 0;
  // the pace: the assignments per variant per day over the observed days (the first to the last)
  const dates = Array.from(new Set(variants.flatMap((v) => (v.usersByDay ?? []).map((d) => d.date)))).sort();
  const span = dates.length >= 1 ? Math.round((Date.parse(`${dates.at(-1)}T00:00:00Z`) - Date.parse(`${dates[0]}T00:00:00Z`)) / 86_400_000) + 1 : 0;
  const assigned = variants.length ? Math.min(...variants.map((v) => (v.usersByDay ?? []).reduce((s, d) => s + Number(d.users ?? 0), 0))) : 0;
  const perDay = span >= 1 && assigned > 0 ? assigned / span : null;
  let required: number | null = null;
  let reason: string | null = null;
  if (!control || Number(control.users ?? 0) === 0) reason = 'no players in the control yet';
  else if (metric.kind === 'proportion') {
    const p1 = control.conversion === null || control.conversion === undefined ? 0 : Number(control.conversion);
    required = sampleSizeProportion(p1, planner.uplift, planner.alpha, planner.betta);
    if (required === null) reason = p1 <= 0 ? 'the control has no conversions yet' : 'not estimable from the control';
  } else {
    // a mean metric: the control's mean and the standard deviation estimated from its conversion and ARPPU (first currency)
    const conv = control.conversion === null || control.conversion === undefined ? 0 : Number(control.conversion);
    const arppu = Number(control.arppu?.[0]?.amount ?? 0);
    const players = metric.key === 'revenue' ? Number(control.exposed ?? 0) : Number(control.users ?? 0);
    const mean = players > 0 ? Number(control.revenue?.[0]?.amount ?? 0) / players : 0;
    required = sampleSizeMean(mean, revenueSdOf(conv, arppu) ?? 0, planner.uplift, planner.alpha, planner.betta);
    if (required === null) reason = 'the control has no revenue yet';
  }
  return { have, required, perDay, daysLeft: required === null ? null : daysFor(required, have, perDay ?? 0), reason };
}
